/**
 * Column sorting for the invoicing lists — one owner for "how does a column of
 * this kind compare" (invoice-list-column-sorting, 2026-08-20).
 *
 * The lists render values that do NOT sort the way they read:
 *   - Reporting Month shows "Jul 2026"; sorted as text that gives Apr, Aug, Dec, Feb.
 *   - Status is a workflow; alphabetically APPROVED comes before DRAFT.
 *   - Payment Status is derived in the UI and has no database column at all.
 *   - Amounts arrive as DRF decimal STRINGS; "9.00" beats "991200.00" as text.
 *
 * Each list declares its columns' kinds and this module owns the comparators, so
 * a fix lands once instead of six times. Blanks always sort LAST, in both
 * directions — a missing due date is unknown, not "earliest".
 */

export const SORT_KIND = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  MONTH: 'month',
  ENUM: 'enum',
  BOOLEAN: 'boolean',
};

// What the lists render in an empty cell. These are placeholders, not values.
const BLANK_PLACEHOLDERS = new Set(['', '-', '—', 'N/A', 'n/a']);

export const isBlank = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return BLANK_PLACEHOLDERS.has(value.trim());
  if (typeof value === 'number') return Number.isNaN(value);
  return false;
};

const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                     'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** A sortable instant for a month, from either an ISO date or a "MMM YYYY" label. */
const monthValue = (value) => {
  if (value instanceof Date) return value.getTime();
  const text = String(value).trim();
  const label = text.match(/^([A-Za-z]{3,})\s+(\d{4})$/);
  if (label) {
    const index = MONTH_NAMES.indexOf(label[1].slice(0, 3).toLowerCase());
    if (index >= 0) return Date.UTC(Number(label[2]), index, 1);
  }
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
};

const numberValue = (value) => {
  if (typeof value === 'number') return value;
  // DRF sends decimals as strings; rendered cells may carry grouping separators.
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
};

const numeric = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Compare two non-blank values of one kind. `column.order` supplies the
 * canonical sequence for ENUM (workflow order for Status, severity for Payment
 * Status) — values outside it sort after the ones we know, never silently
 * mixed in among them.
 */
export const compareByKind = (kind, a, b, column = {}) => {
  switch (kind) {
    case SORT_KIND.NUMBER: {
      const left = numberValue(a);
      const right = numberValue(b);
      if (left === null || right === null) return 0;
      return numeric(left, right);
    }
    case SORT_KIND.DATE: {
      const left = Date.parse(a);
      const right = Date.parse(b);
      if (Number.isNaN(left) || Number.isNaN(right)) return 0;
      return numeric(left, right);
    }
    case SORT_KIND.MONTH: {
      const left = monthValue(a);
      const right = monthValue(b);
      if (left === null || right === null) return 0;
      return numeric(left, right);
    }
    case SORT_KIND.BOOLEAN:
      // false first ascending: what still needs doing rises to the top.
      return numeric(a ? 1 : 0, b ? 1 : 0);
    case SORT_KIND.ENUM: {
      const sequence = column.order || [];
      const left = sequence.indexOf(a);
      const right = sequence.indexOf(b);
      // -1 (unknown to the sequence) must sort AFTER everything known.
      const rank = (index) => (index === -1 ? sequence.length : index);
      return numeric(rank(left), rank(right));
    }
    case SORT_KIND.TEXT:
    default:
      return String(a).localeCompare(String(b), undefined, {
        sensitivity: 'base', numeric: true,
      });
  }
};

/** asc -> desc -> back to the list's own default order. */
export const nextDirection = (direction) =>
  (direction === 'asc' ? 'desc' : direction === 'desc' ? null : 'asc');

/**
 * Reorder rows. Never adds, drops or edits one, and never mutates the caller's
 * array — a sort is a reorder and nothing else.
 */
export const sortRows = (rows, sort, columns) => {
  const list = rows || [];
  if (!sort || !sort.key || !sort.direction) return list;

  const column = (columns || []).find((c) => c.key === sort.key);
  if (!column) return list;

  const read = column.value || ((row) => row[sort.key]);
  const sign = sort.direction === 'desc' ? -1 : 1;

  return list
    .map((row, index) => ({ row, index, value: read(row) }))
    .sort((a, b) => {
      const aBlank = isBlank(a.value);
      const bBlank = isBlank(b.value);
      // Blanks last in BOTH directions, so `sign` deliberately does not apply.
      if (aBlank || bBlank) {
        if (aBlank && bBlank) return a.index - b.index;
        return aBlank ? 1 : -1;
      }
      const compared = compareByKind(column.kind, a.value, b.value, column);
      // Stable: equal values keep the order they arrived in.
      return compared !== 0 ? compared * sign : a.index - b.index;
    })
    .map((entry) => entry.row);
};
